import { useEffect, useState } from "react";
import type { WebsiteSectionKey, WebsiteThemeId } from "@gigflow/shared";
import { websiteSectionKeys } from "@gigflow/shared";
import { apiRequest } from "../api";
import { useAuth } from "../auth";
import { THEME_TOKENS } from "../themes";

type WebsiteSection = {
  key: string;
  title?: string | null;
  content: Record<string, unknown>;
  isEnabled?: boolean;
  sortOrder?: number;
};

type Website = {
  themeId: WebsiteThemeId;
  status: string;
  sectionOrder: string[];
  enabledSections: string[];
  seoTitle?: string | null;
  seoDescription?: string | null;
  sections: WebsiteSection[];
};

export function WebsitePage() {
  const { businessId, business } = useAuth();
  const [website, setWebsite] = useState<Website | null>(null);
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [draftTitle, setDraftTitle] = useState("");
  const [draftContent, setDraftContent] = useState("{}");
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  async function load() {
    if (!businessId) return;
    const data = await apiRequest<{ website: Website }>(
      `/businesses/${businessId}/website`,
    );
    setWebsite(data.website);
  }

  useEffect(() => {
    void load().catch((err: Error) => setError(err.message));
  }, [businessId]);

  async function patchWebsite(body: Record<string, unknown>) {
    if (!businessId) return;
    setPending(true);
    setError(null);
    try {
      const data = await apiRequest<{ website: Website }>(
        `/businesses/${businessId}/website`,
        {
          method: "PATCH",
          body: JSON.stringify(body),
        },
      );
      setWebsite(data.website);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Update failed");
    } finally {
      setPending(false);
    }
  }

  function startEdit(section: WebsiteSection) {
    setEditingKey(section.key);
    setDraftTitle(section.title ?? section.key);
    setDraftContent(JSON.stringify(section.content ?? {}, null, 2));
  }

  async function saveSection() {
    if (!businessId || !editingKey) return;
    setPending(true);
    setError(null);
    try {
      let content: Record<string, unknown>;
      try {
        content = JSON.parse(draftContent) as Record<string, unknown>;
      } catch {
        throw new Error("Section content must be valid JSON");
      }
      await apiRequest(
        `/businesses/${businessId}/website/sections/${editingKey}`,
        {
          method: "PATCH",
          body: JSON.stringify({ title: draftTitle, content }),
        },
      );
      await load();
      setEditingKey(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Section update failed");
    } finally {
      setPending(false);
    }
  }

  if (!businessId)
    return (
      <section>
        <p className="notice">No business selected.</p>
      </section>
    );

  return (
    <>
      <header className="page-header">
        <div>
          <p className="eyebrow">Website</p>
          <h1>Brand & publish</h1>
        </div>
        <div className="cta-row">
          <button
            className="btn btn-primary"
            type="button"
            disabled={pending}
            onClick={() => void patchWebsite({ status: "PUBLISHED" })}
          >
            Publish
          </button>
          <button
            className="btn btn-secondary"
            type="button"
            disabled={pending}
            onClick={() => void patchWebsite({ status: "UNPUBLISHED" })}
          >
            Unpublish
          </button>
          {business?.slug ? (
            <a
              className="btn btn-ghost"
              href={`/site/${business.slug}?preview=1`}
              target="_blank"
              rel="noreferrer"
            >
              Preview
            </a>
          ) : null}
        </div>
      </header>

      {error ? <p className="notice error">{error}</p> : null}

      <section>
        <h2>Status: {website?.status ?? "…"}</h2>
        <h3>Theme</h3>
        <div className="theme-grid">
          {(Object.keys(THEME_TOKENS) as WebsiteThemeId[]).map((id) => {
            const theme = THEME_TOKENS[id];
            return (
              <button
                key={id}
                type="button"
                className={`theme-card ${website?.themeId === id ? "selected" : ""}`}
                onClick={() => void patchWebsite({ themeId: id })}
              >
                <strong>{theme.label}</strong>
                <p className="notice">{theme.description}</p>
              </button>
            );
          })}
        </div>
      </section>

      <section>
        <h2>Sections</h2>
        {(website?.sections ?? []).map((section) => (
          <div className="section-editor" key={section.key}>
            <div className="page-header" style={{ margin: 0 }}>
              <div>
                <strong>{section.title || section.key}</strong>
                <p className="notice">{section.key}</p>
              </div>
              <button
                className="btn btn-ghost"
                type="button"
                onClick={() => startEdit(section)}
              >
                Edit
              </button>
            </div>
            {editingKey === section.key ? (
              <div className="form-grid">
                <label>
                  Title
                  <input
                    value={draftTitle}
                    onChange={(e) => setDraftTitle(e.target.value)}
                  />
                </label>
                <label>
                  Content (JSON)
                  <textarea
                    value={draftContent}
                    onChange={(e) => setDraftContent(e.target.value)}
                    rows={8}
                  />
                </label>
                <div className="cta-row">
                  <button
                    className="btn btn-primary"
                    type="button"
                    disabled={pending}
                    onClick={() => void saveSection()}
                  >
                    Save section
                  </button>
                  <button
                    className="btn btn-ghost"
                    type="button"
                    onClick={() => setEditingKey(null)}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <pre className="preview-box" style={{ maxHeight: 160 }}>
                {JSON.stringify(section.content, null, 2)}
              </pre>
            )}
          </div>
        ))}
        {(website?.sections?.length ?? 0) === 0
          ? websiteSectionKeys.map((key: WebsiteSectionKey) => (
              <p key={key} className="notice">
                {key}
              </p>
            ))
          : null}
      </section>
    </>
  );
}
